// API Service for ClinicIT Backend
// Handles all communication with the Cloudflare Workers backend

const API_BASE_URL = 'https://clinicit-api.khalfaouimanar28.workers.dev';

// Types for API responses
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role?: string;
}

// Auth token management
let authToken: string | null = null;

// Initialize auth token safely
try {
  authToken = localStorage?.getItem('authToken');
} catch (e) {
  // Handle cases where localStorage is not available
  console.warn('localStorage not available');
}

export const setAuthToken = (token: string | null) => {
  authToken = token;
  try {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  } catch (e) {
    // Handle cases where localStorage is not available
    console.warn('localStorage not available for token storage');
  }
};

export const getAuthToken = () => authToken;

// Generic API call function
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // Add auth token if available and not a public endpoint
  if (authToken && !endpoint.startsWith('/public/') && !endpoint.startsWith('/auth/')) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'API request failed',
        message: data.message,
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API call failed:', error);
    return {
      success: false,
      error: 'Network error or server unavailable',
    };
  }
}

// Authentication APIs
export const authAPI = {
  login: async (email: string, password: string) => {
    return apiCall<{ token: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  register: async (userData: any) => {
    return apiCall<{ token: string; user: any }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  logout: async () => {
    const result = await apiCall('/auth/logout', { method: 'POST' });
    setAuthToken(null);
    return result;
  },

  getCurrentUser: async () => {
    return apiCall<any>('/auth/me');
  },
};

// Patients APIs
export const patientsAPI = {
  getAll: async (filters?: any) => {
    const queryParams = filters ? `?${new URLSearchParams(filters)}` : '';
    return apiCall<any[]>(`/patients${queryParams}`);
  },

  getById: async (id: string) => {
    return apiCall<any>(`/patients/${id}`);
  },

  create: async (patientData: any) => {
    return apiCall<any>('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  },

  update: async (id: string, patientData: any) => {
    return apiCall<any>(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    });
  },

  delete: async (id: string) => {
    return apiCall<any>(`/patients/${id}`, {
      method: 'DELETE',
    });
  },
};

// Appointments APIs
export const appointmentsAPI = {
  getAll: async (filters?: any) => {
    const queryParams = filters ? `?${new URLSearchParams(filters)}` : '';
    return apiCall<any[]>(`/appointments${queryParams}`);
  },

  getById: async (id: string) => {
    return apiCall<any>(`/appointments/${id}`);
  },

  create: async (appointmentData: any) => {
    return apiCall<any>('/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  },

  update: async (id: string, appointmentData: any) => {
    return apiCall<any>(`/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(appointmentData),
    });
  },

  delete: async (id: string) => {
    return apiCall<any>(`/appointments/${id}`, {
      method: 'DELETE',
    });
  },

  // Public booking for website visitors
  createPublic: async (appointmentData: any) => {
    return apiCall<any>('/public/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  },
};

// Doctors APIs
export const doctorsAPI = {
  getAll: async (filters?: any) => {
    const queryParams = filters ? `?${new URLSearchParams(filters)}` : '';
    return apiCall<any[]>(`/doctors${queryParams}`);
  },

  getPublic: async () => {
    return apiCall<any[]>('/public/doctors');
  },

  getById: async (id: string) => {
    return apiCall<any>(`/doctors/${id}`);
  },

  create: async (doctorData: any) => {
    return apiCall<any>('/doctors', {
      method: 'POST',
      body: JSON.stringify(doctorData),
    });
  },

  update: async (id: string, doctorData: any) => {
    return apiCall<any>(`/doctors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(doctorData),
    });
  },

  delete: async (id: string) => {
    return apiCall<any>(`/doctors/${id}`, {
      method: 'DELETE',
    });
  },
};

// Services APIs
export const servicesAPI = {
  getAll: async () => {
    return apiCall<any[]>('/services');
  },

  getPublic: async () => {
    return apiCall<any[]>('/public/services');
  },

  getById: async (id: string) => {
    return apiCall<any>(`/services/${id}`);
  },

  create: async (serviceData: any) => {
    return apiCall<any>('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  },

  update: async (id: string, serviceData: any) => {
    return apiCall<any>(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  },

  delete: async (id: string) => {
    return apiCall<any>(`/services/${id}`, {
      method: 'DELETE',
    });
  },
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: async () => {
    return apiCall<any>('/dashboard/stats');
  },

  getRealtimeUpdates: async () => {
    return apiCall<any>('/dashboard/realtime');
  },
};

// Health check
export const healthAPI = {
  check: async () => {
    return apiCall<{ status: string; timestamp: string; version: string }>('/health');
  },
};

// WebSocket-like real-time updates using Durable Objects
export class RealTimeUpdates {
  private intervals: Map<string, NodeJS.Timeout> = new Map();

  subscribe(type: 'appointments' | 'patients' | 'dashboard' | 'doctors' | 'services', callback: (data: any) => void, intervalMs = 30000) {
    const key = `${type}-updates`;
    
    // Clear existing interval if any
    this.unsubscribe(type);

    // Set up polling interval
    const interval = setInterval(async () => {
      try {
        let result;
        switch (type) {
          case 'appointments':
            result = await appointmentsAPI.getAll();
            break;
          case 'patients':
            result = await patientsAPI.getAll();
            break;
          case 'dashboard':
            result = await dashboardAPI.getStats();
            break;
          case 'doctors':
            result = await doctorsAPI.getAll();
            break;
          case 'services':
            result = await servicesAPI.getAll();
            break;
        }

        if (result.success && result.data) {
          callback(result.data);
        }
      } catch (error) {
        console.error(`Real-time update error for ${type}:`, error);
      }
    }, intervalMs);

    this.intervals.set(key, interval);
  }

  unsubscribe(type: 'appointments' | 'patients' | 'dashboard' | 'doctors' | 'services') {
    const key = `${type}-updates`;
    const interval = this.intervals.get(key);
    
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(key);
    }
  }

  unsubscribeAll() {
    this.intervals.forEach((interval) => clearInterval(interval));
    this.intervals.clear();
  }
}

export const realTimeUpdates = new RealTimeUpdates();

// Legacy API object for backward compatibility
export const clinicAPI = {
  // Auth methods
  login: async (credentials: LoginCredentials) => authAPI.login(credentials.email, credentials.password),
  logout: authAPI.logout,
  register: authAPI.register,
  verifyToken: authAPI.getCurrentUser,
  clearToken: () => setAuthToken(null),

  // Data methods
  getAppointments: appointmentsAPI.getAll,
  getPatients: patientsAPI.getAll,
  getDoctors: doctorsAPI.getAll,
  getServices: servicesAPI.getAll,

  // CRUD methods
  createAppointment: appointmentsAPI.create,
  updateAppointment: appointmentsAPI.update,
  deleteAppointment: appointmentsAPI.delete,
  
  createPatient: patientsAPI.create,
  updatePatient: patientsAPI.update,
  deletePatient: patientsAPI.delete
};